import React, { useState } from 'react';
import { btnGhost, btnPrimary, card, muted } from './styles';

function AutoStartNote({ at }) {
  const when = new Date(at);
  const mins = Math.max(0, Math.ceil((when.getTime() - Date.now()) / 60000));
  return (
    <p style={{ fontSize: 12, color: '#92400E', margin: '4px 0 0' }}>
      If you don’t choose, reading starts with all pages at {when.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
      {mins > 0 ? ` (in about ${mins} min)` : ' (any moment now)'}.
    </p>
  );
}

/**
 * Choose which catalog pages to read before any AI reading happens. Unticked pages (covers,
 * "about us", branding, contact pages) are skipped and cost nothing; they can still be read later
 * from the page strip. Thumbnails are the already-rendered pages — no AI involved here.
 *
 * @param autoStartAt ISO instant when reading starts with all pages if nothing is chosen
 */
export default function PagePicker({ pages, onSubmit, busy, autoStartAt }) {
  const [chosen, setChosen] = useState(() => new Set(pages.map((p) => p.pageNumber)));

  const toggle = (n) => setChosen((prev) => {
    const next = new Set(prev);
    if (next.has(n)) next.delete(n); else next.add(n);
    return next;
  });
  const all = () => setChosen(new Set(pages.map((p) => p.pageNumber)));
  const none = () => setChosen(new Set());

  return (
    <section style={card} aria-label="Choose pages to read">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 2px' }}>Choose the pages to read</h2>
          <p style={muted}>
            Untick covers, "about us", branding and contact pages — skipped pages are never sent to AI.
          </p>
          {autoStartAt && <AutoStartNote at={autoStartAt} />}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={btnGhost} onClick={all} disabled={busy}>Select all</button>
          <button style={btnGhost} onClick={none} disabled={busy}>Select none</button>
          <button style={{ ...btnPrimary, opacity: busy || chosen.size === 0 ? 0.6 : 1 }} disabled={busy || chosen.size === 0}
                  onClick={() => onSubmit([...chosen].sort((a, b) => a - b))}>
            {busy ? 'Starting…' : `Read ${chosen.size} of ${pages.length} pages`}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10 }}>
        {pages.map((p) => {
          const on = chosen.has(p.pageNumber);
          return (
            <label key={p.pageNumber}
                   style={{ position: 'relative', display: 'block', cursor: 'pointer', borderRadius: 8, overflow: 'hidden',
                            border: `2px solid ${on ? '#F29213' : '#E5E7EB'}`, background: '#F9FAFB', opacity: on ? 1 : 0.5 }}>
              <img src={p.thumbUrl} alt={`Page ${p.pageNumber}`} loading="lazy"
                   style={{ width: '100%', height: 150, objectFit: 'contain', display: 'block', background: '#fff' }} />
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', fontSize: 12, fontWeight: 600, color: '#374151' }}>
                <input type="checkbox" checked={on} onChange={() => toggle(p.pageNumber)} disabled={busy}
                       style={{ width: 16, height: 16, accentColor: '#F29213' }} />
                Page {p.pageNumber}
              </span>
            </label>
          );
        })}
      </div>
    </section>
  );
}
